import {
  addToast,
  Button,
  OutlinedSelect,
  TextAreaInput,
} from '@octano/global-ui';
import { useCallback, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import AzureFilesUploader from '../../../../components/AzureFilesUploader/AzureFilesUploader';
import { convertToFormFile } from '../../../../components/AzureFilesUploader/utils';
import { TranslationsKeys } from '../../../../locales/translations';
import { StudentsRemovalRequestFormFileChip } from '../../StudentsRemovalRequest/StudentsRemovalRequestForm/StudentsRemovalRequestFormFileChip';
import {
  RequestResolutionState,
  StudentsRemovalPendingRequestFormSubmitKey,
  StudentsRemovalRequestsPendingFormFields,
  StudentsRemovalRequestsPendingFormFile,
} from '../types/removal-requests-pending';
import { useValidations } from '../../../../hooks/useValidations';
import { useHistory } from 'react-router';
import {
  resolveAndChangeStatePendingStudentRemovalRequest,
  resolvePendingStudentRemovalRequest,
} from '../api';
import { PathsLayouts } from '../../../../config/routes';
import { StudentsRemovalRequestPendingSubmitDialog } from './StudentsRemovalRequestPendingSubmitDialog';
import { STUDENT_STATUS } from '../../../../types/studentStatusTypes';

const DEFAULT_VALUES: StudentsRemovalRequestsPendingFormFields = {
  state: null,
  comment: '',
  files: [],
};

export function StudentsRemovalRequestPendingForm({
  studentRemovalRequestId,
  studentStatus,
}: {
  studentRemovalRequestId: number;
  studentStatus: string;
}) {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState<{
    resolve: boolean;
    resolveAndChangeState: boolean;
  }>({
    resolve: false,
    resolveAndChangeState: false,
  });
  const [openSubmit, setOpenSubmit] = useState(false);

  const { t } = useTranslation(
    TranslationsKeys.STUDENT_MANAGEMENT_STUDENT_REMOVAL_REQUEST_MANAGEMENT_DETAILS,
  );

  const texts = useMemo(
    () => ({
      requestResolution: t('form.requestResolution'),
      state: t('form.state'),
      statePlaceholder: t('form.statePlaceholder'),
      optionalComplementaryFiles: t('form.optionalComplementaryFiles'),
      save: t('form.save'),
      error: t('form.error'),
      success: t('form.success'),
    }),
    [t],
  );
  const [filesState, setFilesState] = useState<number>(0);

  const {
    control,
    watch,
    handleSubmit,
  } = useForm<StudentsRemovalRequestsPendingFormFields>({
    defaultValues: DEFAULT_VALUES,
  });

  const files = watch('files');
  const state = watch('state');
  const { msgValidations } = useValidations();

  const supplementaryFiles = useFieldArray({
    control,
    name: 'files',
  });

  const handleAddFile = useCallback(
    (file: StudentsRemovalRequestsPendingFormFile) => {
      supplementaryFiles.append(file);
      setFilesState((prev) => prev + 1);
    },
    [supplementaryFiles],
  );
  const handleRemoveFile = useCallback(
    (index: number) => {
      supplementaryFiles.remove(index);
      setFilesState((prev) => prev + 1);
    },
    [supplementaryFiles],
  );

  const onSubmit = useCallback(
    async (
      key: StudentsRemovalPendingRequestFormSubmitKey,
      values: StudentsRemovalRequestsPendingFormFields,
    ) => {
      setIsLoading((prev) => ({
        ...prev,
        [key]: true,
      }));

      const payload = {
        studentRemovalRequestId,
        status: values.state?.value as string,
        comment: values.comment,
        filesIds: values?.files?.map((e) => e.id) || [],
      };

      const { error } =
        key === StudentsRemovalPendingRequestFormSubmitKey.RESOLVE
          ? await resolvePendingStudentRemovalRequest(payload)
          : await resolveAndChangeStatePendingStudentRemovalRequest(payload);

      setIsLoading((prev) => ({
        ...prev,
        [key]: false,
      }));

      setOpenSubmit(false);

      addToast({
        icon: error ? 'error' : 'check',
        color: error ? 'danger' : 'success',
        text: error ? texts.error : texts.success,
      });

      if (!error) {
        history.push(PathsLayouts.studentsRemovalRequestsManage);
      }
    },
    [studentRemovalRequestId, texts, history],
  );

  const openSubmitDialog = useCallback(async () => {
    if (!state) {
      return;
    }

    if (
      studentStatus !== STUDENT_STATUS.REGULAR ||
      state?.value !== RequestResolutionState.APPROVED
    ) {
      return await handleSubmit((values) =>
        onSubmit(StudentsRemovalPendingRequestFormSubmitKey.RESOLVE, values),
      )();
    }

    setOpenSubmit(true);
  }, [state, studentStatus, handleSubmit, onSubmit]);

  return (
    <form onSubmit={handleSubmit(openSubmitDialog)}>
      <Row>
        <Col xs={12}>
          <p className="fs-16 text-uppercase text-primary fw-700 mb-0">
            {texts.requestResolution}
          </p>
        </Col>
        <Col xs={12} className="mt-3">
          <TextAreaInput
            name="comment"
            control={control}
            label={''}
            disabled={isLoading.resolve || isLoading.resolveAndChangeState}
          />
        </Col>
      </Row>
      <Row className="mt-5">
        <Col xs={12} md={6} lg={6}>
          <div key={filesState.toString()} style={{ marginTop: '-9px' }}>
            <AzureFilesUploader
              label={texts.optionalComplementaryFiles}
              onUploaded={(next) => handleAddFile(convertToFormFile(next))}
            />
            {!!files?.length && (
              <div
                className="w-full mt-3 d-flex flex-wrap align-items-center justify-content-start"
                style={{ gap: 5 }}
              >
                {files?.map((file, i) => (
                  <StudentsRemovalRequestFormFileChip
                    key={file.id.toString()}
                    value={i}
                    children={file.name}
                    onRemove={handleRemoveFile}
                  />
                ))}
              </div>
            )}
          </div>
        </Col>
        <Col xs={12} md={6} lg={3} className="ml-auto">
          <OutlinedSelect
            control={control}
            name="state"
            label={texts.state}
            placeholder={texts.statePlaceholder}
            rules={{ required: msgValidations.required }}
            options={[
              { label: 'Aprobada', value: RequestResolutionState.APPROVED },
              { label: 'Rechazada', value: RequestResolutionState.REJECTED },
            ]}
            disabled={isLoading.resolve || isLoading.resolveAndChangeState}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={3} className="mt-3 ml-auto">
          <Button
            type="submit"
            text={texts.save}
            color="primary"
            fullwidth
            loading={isLoading.resolve || isLoading.resolveAndChangeState}
          />
        </Col>
      </Row>
      {openSubmit && (
        <StudentsRemovalRequestPendingSubmitDialog
          handleClose={() => setOpenSubmit(false)}
          isLoading={isLoading}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
        />
      )}
    </form>
  );
}
